import { useState, useEffect } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";

const KIND_OPTIONS = [
  { label: "Dynamic", bit: 1 },
  { label: "Overlay", bit: 2 },
  { label: "Banner", bit: 4 },
  { label: "Static", bit: 8 },
];

const TYPE_OPTIONS = [
  { value: "vast", label: "VAST" },
  { value: "rtb", label: "RTB" },
];

const DEFAULT_FORM = {
  name: "",
  alias: "",
  types: ["vast"],
  kinds: 0,
  cpm: 0,
  allowTransit: false,
  settings: { transitBufferMs: "", issueViewerCookie: false },
};

const API_PREFIX = "/api";
const API_BASE = import.meta.env?.VITE_API_URL ?? API_PREFIX;

// Region endpoints for publisher serving/reporting URLs. Add a region by
// appending one entry here — the UI renders a copyable link per region
// automatically. `origin: null` = the current dashboard host (GLOBAL keeps
// today's behavior); a string origin points the links at that regional host.
const REGIONS = [
  { label: "GLOBAL", origin: null },
  { label: "AMERICAS", origin: "https://us.app.adlywise.com" },
  // e.g. { label: "EUROPE", origin: "https://eu.app.adlywise.com" },
];

const buildServingUrl = (protocol, alias, token, origin) => {
  // Publisher serving URLs live at the host root, not under /api (the backend
  // serves /vast and /rtb directly). Strip a trailing /api (or /api/) from a
  // configured API base, but keep the bare "/api" default so dev requests
  // still reach the backend through the proxy.
  let base = API_BASE.replace(/\/+$/, "");
  if (base !== API_PREFIX && base.endsWith(API_PREFIX)) {
    base = base.slice(0, -API_PREFIX.length);
  }
  const path = `${base}/${protocol}/${alias}/${token}`;
  try {
    return new URL(path, origin || window.location.origin).href;
  } catch {
    return path;
  }
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const fromProvider = (p) => ({
  name: p.name ?? "",
  alias: p.alias ?? "",
  types: Array.isArray(p.types) && p.types.length ? p.types : ["vast"],
  kinds: p.kinds ?? 0,
  cpm: p.cpm ?? 0,
  allowTransit: p.allowTransit ?? false,
  settings: {
    transitBufferMs:
      p.settings?.transitBufferMs != null
        ? String(p.settings.transitBufferMs)
        : "",
    issueViewerCookie: Boolean(p.settings?.issueViewerCookie),
  },
});

function ServingUrl({ label, url }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this URL", url);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="min-w-[72px] text-xs font-bold uppercase text-(--text-muted)">
        {label}
      </span>
      <code className="flex-1 truncate rounded-(--radius-sm) bg-(--surface-2) px-2 py-1.5 text-xs text-(--text-primary)">
        {url}
      </code>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center justify-center rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-1.5 text-(--text-secondary) transition-colors hover:bg-(--surface-muted)"
        title="Copy URL"
        aria-label={`Copy ${label} URL`}
      >
        {copied ? (
          <Check size={15} className="text-(--status-success)" />
        ) : (
          <Copy size={15} />
        )}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-1.5 text-(--text-secondary) transition-colors hover:bg-(--surface-muted)"
        title="Open URL"
        aria-label={`Open ${label} URL`}
      >
        <ExternalLink size={15} />
      </a>
    </div>
  );
}

// A protocol's publisher URL, rendered as one copyable row per region (REGIONS).
function ServingUrlGroup({ title, protocol, alias, token }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
        {title}
      </span>
      <div className="flex flex-col gap-2">
        {REGIONS.map((region) => (
          <ServingUrl
            key={region.label}
            label={region.label}
            url={buildServingUrl(protocol, alias, token, region.origin)}
          />
        ))}
      </div>
    </div>
  );
}

export default function ProviderForm({ initialProvider, onSubmit, onCancel }) {
  const [form, setForm] = useState(() =>
    initialProvider ? fromProvider(initialProvider) : { ...DEFAULT_FORM },
  );
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(
      initialProvider ? fromProvider(initialProvider) : { ...DEFAULT_FORM },
    );
    setError("");
  }, [initialProvider]);

  const toggleKind = (bit) => {
    setForm((prev) => ({ ...prev, kinds: prev.kinds ^ bit }));
  };

  const toggleType = (value) => {
    setForm((prev) => {
      const nextTypes = prev.types.includes(value)
        ? prev.types.filter((t) => t !== value)
        : [...prev.types, value];
      return {
        ...prev,
        types: nextTypes,
        allowTransit: !nextTypes.includes("rtb") ? false : prev.allowTransit,
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    if (form.types.length === 0) {
      setError("Select at least one type");
      return;
    }
    setError("");
    const hasRtb = form.types.includes("rtb");
    const hasVast = form.types.includes("vast");
    onSubmit({
      name: form.name.trim(),
      alias: form.alias.trim() || null,
      types: form.types,
      kinds: form.kinds,
      cpm: Number(form.cpm) || 0,
      allowTransit: hasRtb ? form.allowTransit : false,
      settings: {
        transitBufferMs:
          hasRtb && form.settings.transitBufferMs !== ""
            ? Number(form.settings.transitBufferMs)
            : null,
        issueViewerCookie: hasVast
          ? Boolean(form.settings.issueViewerCookie)
          : false,
      },
    });
  };

  const savedAlias = initialProvider?.alias ?? "";
  const savedToken = initialProvider?.secretToken ?? "";
  const savedTypes = Array.isArray(initialProvider?.types)
    ? initialProvider.types
    : [];
  const canShowUrls = Boolean(savedAlias && savedToken);
  const showVastUrl = canShowUrls && savedTypes.includes("vast");
  const showRtbUrl = canShowUrls && savedTypes.includes("rtb");

  return (
    <form
      className="builder-form"
      style={{ gap: "0.5rem" }}
      onSubmit={handleSubmit}
    >
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      <div className="form-group-modal">
        <label htmlFor="provider-name">Name *</label>
        <input
          id="provider-name"
          value={form.name}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, name: e.target.value }))
          }
          placeholder="e.g. Acme Ad Network"
          required
        />
      </div>

      <div className="form-group-modal">
        <label htmlFor="provider-alias">Alias</label>
        <input
          id="provider-alias"
          value={form.alias}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, alias: slugify(e.target.value) }))
          }
          placeholder="e.g. acme-network"
        />
        <p className="form-help-text">
          URL-safe identifier used in serving links (lowercase letters, numbers,
          hyphens).
        </p>
      </div>

      <div className="form-group-modal">
        <label>Type</label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginTop: "0.25rem",
          }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.types.includes(opt.value)}
                onChange={() => toggleType(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {form.types.includes("rtb") && (
        <div className="form-group-modal">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.allowTransit}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, allowTransit: e.target.checked }))
              }
            />
            <span>Allow transit campaigns</span>
          </label>
          <p className="form-help-text">
            Enables this publisher to participate as a transit endpoint.
          </p>
        </div>
      )}

      {form.types.includes("rtb") && (
        <div className="form-group-modal">
          <label htmlFor="provider-transit-buffer">
            Transit await buffer (ms)
          </label>
          <input
            id="provider-transit-buffer"
            type="number"
            min="0"
            step="1"
            value={form.settings.transitBufferMs}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                settings: { ...prev.settings, transitBufferMs: e.target.value },
              }))
            }
            placeholder="e.g. 200"
          />
          <p className="form-help-text">
            Optional. Maximum time the transit layer waits before forwarding the
            bid request.
          </p>
        </div>
      )}

      {form.types.includes("vast") && (
        <div className="form-group-modal">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.settings.issueViewerCookie}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    issueViewerCookie: e.target.checked,
                  },
                }))
              }
            />
            <span>Issue web-viewer cookie (frequency capping)</span>
          </label>
          <p className="form-help-text">
            Issues a partitioned viewer cookie (fingerprint fallback) so web
            viewers can be frequency-capped. Requires the global feature flag.
          </p>
        </div>
      )}

      {(showVastUrl || showRtbUrl) && (
        <div className="form-group-modal">
          <label>Serving URLs</label>
          <div className="flex flex-col gap-2">
            {showVastUrl && (
              <ServingUrlGroup
                title="vast"
                protocol="vast"
                alias={savedAlias}
                token={savedToken}
              />
            )}
            {showRtbUrl && (
              <ServingUrlGroup
                title="rtb"
                protocol="rtb"
                alias={savedAlias}
                token={savedToken}
              />
            )}
          </div>
        </div>
      )}

      {canShowUrls && (
        <div className="form-group-modal">
          <label>Reporting URL</label>
          <div className="flex flex-col gap-2">
            <ServingUrlGroup
              title="stats"
              protocol="report/publisher"
              alias={savedAlias}
              token={savedToken}
            />
          </div>
          <p className="form-help-text">
            Daily stats (bids, impressions, owed). Append{" "}
            <code>?from=YYYY-MM-DD&amp;to=YYYY-MM-DD</code> to filter the date
            range.
          </p>
        </div>
      )}

      <div className="form-group-modal">
        <label>Ad kinds</label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginTop: "0.25rem",
          }}
        >
          {KIND_OPTIONS.map(({ label, bit }) => (
            <label
              key={bit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={(form.kinds & bit) !== 0}
                onChange={() => toggleKind(bit)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="form-group-modal">
        <label htmlFor="provider-cpm">CPM ($)</label>
        <input
          id="provider-cpm"
          type="number"
          min="0"
          step="0.01"
          value={form.cpm}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              cpm: e.target.value,
            }))
          }
          placeholder="e.g. 2.50"
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "flex-end",
          marginTop: "1.5rem",
        }}
      >
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialProvider ? "Save Changes" : "Create Publisher"}
        </button>
      </div>
    </form>
  );
}
